<template>
  <section class="todo-overview">
    <div class="row grid-align-center grid-padding callout" v-if="enhancedToDos.length === 0">
      <div class="col-xs-8" v-if="!archive">Er staan geen acties open.</div>
      <div class="col-xs-8" v-else>Er zijn gearchiveerde acties.</div>
    </div>
    <table class="table" v-if="enhancedToDos.length > 0">
      <colgroup>
        <col v-for="n in 6" :key="n" :class="'table-col-' + n++" />
      </colgroup>
      <thead class="head">
        <tr>
          <th><span class="hidden">Status</span></th>
          <th>
            <span @click.prevent="toggleSelectedSort('description')">
              Omschrijving
              <v-sort-ascending-icon v-if="getSort('description').ascending" :class="{'inactive-sort': !isSelectedSort('description')}"></v-sort-ascending-icon>
              <v-sort-descending-icon v-else :class="{'inactive-sort': !isSelectedSort('description')}"></v-sort-descending-icon>
            </span>
          </th>
          <th>
            <span @click.prevent="toggleSelectedSort('customer')">
              Patiënt
              <v-sort-ascending-icon v-if="getSort('customer').ascending" :class="{'inactive-sort': !isSelectedSort('customer')}"></v-sort-ascending-icon>
              <v-sort-descending-icon v-else :class="{'inactive-sort': !isSelectedSort('customer')}"></v-sort-descending-icon>
            </span>
          </th>
          <th>
            <span @click.prevent="toggleSelectedSort('user')">
              Taak voor
              <v-sort-ascending-icon v-if="getSort('user').ascending" :class="{'inactive-sort': !isSelectedSort('user')}"></v-sort-ascending-icon>
              <v-sort-descending-icon v-else :class="{'inactive-sort': !isSelectedSort('user')}"></v-sort-descending-icon>
            </span>
          </th>
          <td>
            <span @click.prevent="toggleSelectedSort('date')">
              Datum
              <v-sort-ascending-icon v-if="getSort('date').ascending" :class="{'inactive-sort': !isSelectedSort('date')}"></v-sort-ascending-icon>
              <v-sort-descending-icon v-else :class="{'inactive-sort': !isSelectedSort('date')}"></v-sort-descending-icon>
            </span>
          </td>
          <td><span class="hidden">Remove</span></td>
        </tr>
      </thead>
      <tbody
        v-for="(todo, index) in enhancedToDos"
        :key="index"
        :class="{ 'action-required': false }"
      >
        <tr>
          <td class="icon" :class="{ 'archiveClass': archive }"><img :src="todo.typeInfo.icon" class="icon" /></td>
          <td :class="{ 'archiveClass': archive }">
            <a
              @click.prevent="goToPath(todo)"
              class="description-link"
              href="">{{todo.typeInfo.title}}</a>
          </td>
          <td :class="{ 'archiveClass': archive }">
            <a
              @click.prevent="goToPatientDashboard(todo.customerId)"
              class="patient-link"
              href=""
              >{{todo.customerName}}
            </a>
          </td>
          <td :class="{ 'archiveClass': archive }">{{todo.assignedToUserName}}</td>
          <td :class="{ 'archiveClass': archive }">{{todo.activateOn | date}}</td>
          <td class="icon actionIcons">
            <v-close-icon
              class="close"
              v-if="todo.isClosable"
              @click.prevent="deleteToDo(todo.id)"
              v-tooltip="{content: 'Actie verwijderen', placement: 'bottom', html: false}"
            ></v-close-icon>            
            <v-archive-icon
              v-if="!archive && todo.isArchivable"
              class="archive"
              @click.prevent="archiveToDo(todo.id)"
              v-tooltip="{content: 'Actie archiveren', placement: 'bottom', html: false}"
            ></v-archive-icon>
            <v-unarchive-icon
            v-if="archive && todo.isArchive"
              class="unarchive"
              @click.prevent="unArchiveToDo(todo.id)"
              v-tooltip="{content: 'Actie herstellen', placement: 'bottom', html: false}"
            ></v-unarchive-icon>
          </td>
        </tr>
        <tr v-if="todo.typeInfo.description">
          <td></td>
          <td colspan="4" :class="{ 'archiveClass': archive }">
            {{todo.typeInfo.description}}
            <div class="comment-section" v-if="todo.comment"><b>Opmerking: </b> {{todo.comment}}</div>
          </td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <div class="row grid-justify-content-center" v-if="hasMoreToDos">
      <div class="col-xs-12 col-md-12">
        <v-button hollow md class="filter-count-button" @click.native="toggleShowAllToDos">{{toggleShowAllToDosText}}</v-button>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';
import dateFilter from '@/filters/DateString'
import { mapActions } from 'vuex'
import router from '@/router';
import { EventBus } from '@/components/shared/event-bus';
import { pushToDoClosedEvent } from './toDoClosedEventStream';
import { pushToDoArchiveEvent, pushToDoUnArchiveEvent } from './toDoArchiveEventStream';
import VCloseIcon from 'icons/CloseCircleOutline';
import VArchiveIcon from 'icons/ArchiveArrowDownOutline';
import VUnarchiveIcon from 'icons/ArchiveArrowUpOutline';
import { getToDoTypeInfo } from './toDoTypeInfo';
import VSortDescendingIcon from 'icons/SortAscending'
import VSortAscendingIcon from 'icons/SortDescending'
import { currentUserIdentity$, getOsaOverviewDetails } from '@/services';

function initSort (valueFn) {
  return {
    valueFn: valueFn,
    ascending: true
  };
}

export default {
  name: 'ToDoList',
  subscriptions () {
    return {
      user: currentUserIdentity$
    };
  },
  components: {
    VCloseIcon,
    VArchiveIcon,
    VUnarchiveIcon,
    VSortAscendingIcon,
    VSortDescendingIcon
  },
  props: {
    toDos: Array,
    title: String,
    archive: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    const sorting = {
      'description': initSort(todo => todo.typeInfo.title + todo.typeInfo.description),
      'customer': initSort(todo => todo.customerName),
      'user': initSort(todo => todo.assignedToUserName),
      'date': initSort(todo => todo.activateOn)
    };

    return {
      showAllToDos: false,
      nrOfInitialToDos: 5,
      sorting: sorting,
      selectedSortId: 'description'
    }
  },
  computed: {
    selectedSort () {
      return this.getSort(this.selectedSortId);
    },
    enhancedToDos () {
      const showNrOfToDos = (this.showAllToDos ? 1000 : this.nrOfInitialToDos);
      return this.toDos
        .map(toDo => {
          return {
            ...toDo,
            ...{
              typeInfo: getToDoTypeInfo(toDo)
            }
          };
        })
        .sort((left, right) => {
          const leftValue = this.selectedSort.valueFn(left);
          const rightValue = this.selectedSort.valueFn(right);

          return this.selectedSort.ascending
            ? leftValue.localeCompare(rightValue)
            : rightValue.localeCompare(leftValue);
        })
        .filter((toDo, index) => {
          return index < showNrOfToDos;
        });
    },
    hasMoreToDos () {
      return this.toDos.length > this.nrOfInitialToDos;
    },
    toggleShowAllToDosText () {
      if (this.showAllToDos) {
        return 'Toon minder';
      }

      return `Toon meer (${this.toDos.length})`;
    }
  },
  filters: {
    date: dateFilter
  },
  methods: {
    ...mapActions({
      setActivePatient: 'patient/setActivePatient'
    }),
    toggleSelectedSort (sortProperty) {
      if (sortProperty === this.selectedSortId) {
        this.selectedSort.ascending = !this.selectedSort.ascending;
      } else {
        this.selectedSortId = sortProperty;
      }
    },
    getSort (sortId) {
      return this.sorting[sortId];
    },
    isSelectedSort (sortId) {
      return this.selectedSort === this.getSort(sortId);
    },
    /**
     * @function deleteToDo
     * @param {number} - unique id of the todo
     * @desc Removes a todo item
     */
     deleteToDo (id) {
      new Promise((resolve, reject) => {
        EventBus.$emit('prompt', {
          title: 'Actie verwijderen?',
          message: 'U staat op het punt om een actie te sluiten. Weet u het zeker?',
          resolve,
          reject
        })
      })
      .then(() => {
        return axios.post('Todolist/Delete', { ToDoId: id }).then((response) => {
          // Commands dont have return values,
          // Events will appear automatically in the future
          // Now: we have to assume this method is called because it was allowed to, and the todo is actually closed.
          pushToDoClosedEvent({
            toDoId: id
          });
        });
      })
      .catch(() => {})
    },
    /**
     * @function archiveToDo
     * @param {number} - unique id of the todo
     * @desc Archives a todo item
     */
     archiveToDo (id) {
      return axios.post('Todolist/Archive', { ToDoId: id, Archive: true }).then((response) => {
        // Commands dont have return values,
        // Events will appear automatically in the future
        // Now: we have to assume this method is called because it was allowed to, and the todo is actually closed.
        EventBus.$emit('alert', {
          message: 'De actie is verplaatst naar tabblad Gearchiveerde acties.'
        })
        pushToDoArchiveEvent({});
      });
    },
    /**
     * @function unArchiveToDo
     * @param {number} - unique id of the todo
     * @desc Un archive a todo item
     */
     unArchiveToDo (id) {
      return axios.post('Todolist/Archive', { ToDoId: id, Archive: false }).then((response) => {
        // Commands dont have return values,
        // Events will appear automatically in the future
        // Now: we have to assume this method is called because it was allowed to, and the todo is actually closed.
        EventBus.$emit('alert', {
          message: 'De actie is hersteld naar tabblad “Mijn Acties” of “Acties van Afdeling”.'
        })
        pushToDoUnArchiveEvent({});
      });
    },
    /**
     * @function goToPatientDashboard
     * @param {Number} - patientId
     */
    async goToPatientDashboard (patientId) {
      await this.setActivePatient(patientId);
      await router.push('/patientdashboard')
    },
    async goToPath (toDo) {
      await this.setActivePatient(toDo.customerId);

      if (toDo.typeInfo.actionActivatesOsaEvaluationId) {
        await getOsaOverviewDetails({
          osaType: 'Evaluation',
          osaId: toDo.osaEvaluationId,
          licenseId: 0
        })
      }

      if (toDo.typeInfo.actionUrl) {
        await router.push(toDo.typeInfo.actionUrl);
      }
    },
    toggleShowAllToDos () {
      this.showAllToDos = !this.showAllToDos;
    }
  }
};
</script>

<style lang="scss" scoped>

// Width of each table column
$table-columns: (
  1: 40px,
  2: 30%,
  3: 30%,
  4: 15%,
  5: 15%,
  6: 50px,
) !default;

.todo-overview {
  h1 {
    color: $heading-color;
    font-size: $heading3-font-size;
    font-weight: $heading-font-weight;
    line-height: $heading3-line-height;
    margin-top: $heading-margin-top;
    margin-bottom: 20px;
  }
  .filter-count-button {
    align-self: center;
  }
  table {
    text-align: auto !important;
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    text-align: left;

    // Generate classes for each column and assign width
    @each $table-column-key, $table-column-value in $table-columns {
      .table-col-#{$table-column-key} {
        width: $table-column-value;
      }
    }

    > thead > tr {
      background-color: $table-header-background;
      border: 1px solid $gray-light;

      > td,
      > th {
        vertical-align: baseline;
        text-align: left;
        padding: 10px;
        color: $gray-darkest;
        font-size: type-scale(0);
        line-height: line-height(0);
        font-weight: 900;
        > span {
          color: $gray-darkest;
          cursor: pointer;
        }
        > span > .material-design-icon {
          display: inline-flex;
          vertical-align: middle;
          margin-left: 5px;
          justify-content: center;

          /deep/ svg {
            display: block;
            fill: #66a5b5;
          }
        }

        .icon-menu-down {
          fill: $table-row-icon-menu-down;
          margin-top: 0;
          transition: $table-transition-rotate;
        }
      }
    }

    > tbody {
      background: $white;
      border: 1px solid $gray-light;

      > tr {
        > td,
        > th {
          vertical-align: baseline;
          text-align: left;
          padding: 16px 10px;
        }
      }
      &.action-required {
        background-color: $pink;
      }
    }

    .link {
      text-decoration: none;
      color: $teal-darkest;
      font-weight: 900;
      border: none;
      display: inline-block;

      white-space: normal;
      &:hover {
        text-decoration: underline;
      }
    }

    .hidden {
      display: none;
    }

    .description-link {
      color: $teal-darkest;
      font-weight: 900;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    .patient-link {
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }

      &:visited {
        color: $gray-darkest;
      }
    }

    .icon {
      width: 24px;
      height: 24px;
    }
    .actionIcons {
      vertical-align: top;
      min-width: 100px !important;
    }
    .icon {
      width: 24px;
      height: 24px;
      vertical-align: top;
      width: 50px;

      /deep/ .material-design-icon {
        width: 24px;
        margin: -4px 8px -4px 4px;

        &.pending {
          color: $gray;
        }

        &.alert {
          color: $orange;
        }
        &.close, &.archive, &.unarchive {
          color: $teal-darkest;
          float: right;
          &:hover {
            color: $teal-dark;
            cursor: pointer;
          }
        }

        svg {
          display: block;
          width: 30px;
          height: 30px;
          fill: currentColor;
        }
      }
    }
  }
  .is-expanded {
    .icon-menu-down {
      transform: rotate(-180deg);
    }
  }
  .inactive-sort {
    opacity: 0.5;
  }
  .archiveClass {
    pointer-events: none;
    color: $gray-light;
    background-color: transparent;
    transition: all 0.3s;
    a {
      color: $gray-light ;
      background-color: transparent;
      transition: all 0.3s;      
    }
    img {
      opacity: 0.5;
    }
    > .patient-link {
      color: $gray-light !important;
    }    
  }
  .comment-section {
    font-size: 12px;
    color: $teal-darkest;
    background-color: $cyan-super-light;
    word-wrap: break-word;
    padding: 12px;
    margin-top: 8px;
    width: fit-content;
    min-width: 250px; 
  }
}

</style>
